<template>
  <div class="user-device-analysis">
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" label="Loading analysis..." />
      <p class="mt-2">Analyzing user device patterns...</p>
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle mr-2" />
      {{ error }}
    </div>
    
    <div v-else-if="!deviceAnalysis" class="text-center py-4">
      <i class="fas fa-chart-line fa-3x text-muted mb-3" />
      <h5>No Analysis Available</h5>
      <p class="text-muted">No analysis data found for this user.</p>
    </div>
    
    <div v-else>
      <!-- Risk Assessment Header -->
      <div class="risk-assessment mb-4">
        <div class="row">
          <div class="col-md-8">
            <div class="card border-left-primary">
              <div class="card-body">
                <div class="row no-gutters align-items-center">
                  <div class="col mr-2">
                    <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                      Risk Assessment
                    </div>
                    <div class="h5 mb-0 font-weight-bold text-gray-800">
                      <b-badge
                        :variant="getRiskVariant(deviceAnalysis.risk_level)"
                        class="px-3 py-2"
                        style="font-size: 1rem;"
                      >
                        {{ deviceAnalysis.risk_level }} RISK
                      </b-badge>
                    </div>
                    <div class="mt-2 text-muted">
                      {{ deviceAnalysis.reason }}
                    </div>
                  </div>
                  <div class="col-auto">
                    <i
                      class="fas fa-2x text-gray-300"
                      :class="getRiskIcon(deviceAnalysis.risk_level)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card border-left-info">
              <div class="card-body">
                <div class="row no-gutters align-items-center">
                  <div class="col mr-2">
                    <div class="text-xs font-weight-bold text-info text-uppercase mb-1">
                      Analysis Period
                    </div>
                    <div class="h5 mb-0 font-weight-bold text-gray-800">
                      30 Days
                    </div>
                    <div class="text-muted">
                      Recent activity
                    </div>
                  </div>
                  <div class="col-auto">
                    <i class="fas fa-calendar fa-2x text-gray-300" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="row mb-4">
        <div class="col-lg-3 col-md-6 mb-4">
          <div class="card border-left-primary shadow h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Total Devices
                  </div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                    {{ deviceAnalysis.device_count }}
                  </div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-mobile-alt fa-2x text-gray-300" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3 col-md-6 mb-4">
          <div class="card border-left-success shadow h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Total Logins
                  </div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                    {{ deviceAnalysis.total_logins }}
                  </div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-sign-in-alt fa-2x text-gray-300" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3 col-md-6 mb-4">
          <div class="card border-left-info shadow h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Avg Logins/Device
                  </div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                    {{ Math.round(deviceAnalysis.avg_logins_per_device) }}
                  </div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-chart-bar fa-2x text-gray-300" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3 col-md-6 mb-4">
          <div class="card border-left-warning shadow h-100 py-2">
            <div class="card-body">
              <div class="row no-gutters align-items-center">
                <div class="col mr-2">
                  <div class="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Active Devices
                  </div>
                  <div class="h5 mb-0 font-weight-bold text-gray-800">
                    {{ activeDevicesCount }}
                  </div>
                </div>
                <div class="col-auto">
                  <i class="fas fa-wifi fa-2x text-gray-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detailed Device Analysis -->
      <div class="card mb-4">
        <div class="card-header">
          <h6 class="m-0 font-weight-bold text-primary">
            <i class="fas fa-list mr-2" />
            Device Details
          </h6>
        </div>
        <div class="card-body">
          <div v-if="deviceAnalysis.devices && deviceAnalysis.devices.length > 0">
            <div class="table-responsive">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>First Login</th>
                    <th>Last Login</th>
                    <th>Login Count</th>
                    <th>Status</th>
                    <th>Activity</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(device, index) in deviceAnalysis.devices"
                    :key="index"
                    :class="getDeviceRowClass(device)"
                  >
                    <td>
                      <div class="d-flex align-items-center">
                        <i :class="getDeviceIcon(device.device_name)" class="mr-2" />
                        <div>
                          <div class="font-weight-bold">{{ device.device_name }}</div>
                          <small class="text-muted">{{ device.fingerprint }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <small>{{ formatDate(device.first_login) }}</small>
                    </td>
                    <td>
                      <small>{{ formatDate(device.last_login) }}</small>
                    </td>
                    <td>
                      <b-badge
                        :variant="getLoginCountVariant(device.login_count)"
                        class="px-2 py-1"
                      >
                        {{ device.login_count }}
                      </b-badge>
                    </td>
                    <td>
                      <b-badge
                        :variant="getDeviceStatusVariant(device)"
                        class="px-2 py-1"
                      >
                        {{ getDeviceStatus(device) }}
                      </b-badge>
                    </td>
                    <td>
                      <div class="progress" style="height: 20px;">
                        <div
                          class="progress-bar"
                          :class="'bg-' + getActivityVariant(device)"
                          :style="{ width: getActivityPercentage(device) + '%' }"
                        >
                          {{ Math.round(getActivityPercentage(device)) }}%
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="text-center py-4">
            <p class="text-muted">No detailed device information available.</p>
          </div>
        </div>
      </div>

      <!-- Recommendations -->
      <div class="card">
        <div class="card-header">
          <h6 class="m-0 font-weight-bold text-primary">
            <i class="fas fa-lightbulb mr-2" />
            Recommendations
          </h6>
        </div>
        <div class="card-body">
          <div class="recommendations">
            <div
              v-for="(recommendation, index) in getRecommendations()"
              :key="index"
              class="recommendation-item mb-3 p-3 rounded"
              :class="recommendation.class"
            >
              <div class="d-flex align-items-start">
                <i :class="recommendation.icon + ' mt-1 mr-3'" />
                <div>
                  <h6 class="mb-1">{{ recommendation.title }}</h6>
                  <p class="mb-0">{{ recommendation.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'UserDeviceAnalysis',
  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    userEmail: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters('deviceTracking', [
      'deviceAnalysis',
      'loading',
      'error'
    ]),
    activeDevicesCount() {
      if (!this.deviceAnalysis?.devices) return 0;
      
      return this.deviceAnalysis.devices.filter(device => {
        const lastLogin = new Date(device.last_login);
        const now = new Date();
        const diffHours = (now - lastLogin) / (1000 * 60 * 60);
        return diffHours < 168; // Active within last 7 days
      }).length;
    }
  },
  methods: {
    ...mapActions('deviceTracking', ['fetchDeviceAnalysis']),
    
    getRiskVariant(riskLevel) {
      switch (riskLevel) {
        case 'HIGH': return 'danger';
        case 'MEDIUM': return 'warning';
        case 'LOW': return 'success';
        default: return 'secondary';
      }
    },
    
    getRiskIcon(riskLevel) {
      switch (riskLevel) {
        case 'HIGH': return 'fa-exclamation-triangle';
        case 'MEDIUM': return 'fa-exclamation-circle';
        case 'LOW': return 'fa-check-circle';
        default: return 'fa-question-circle';
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString();
      }
    },
    
    getDeviceIcon(deviceName) {
      const name = deviceName.toLowerCase();
      if (name.includes('iphone')) return 'fab fa-apple text-dark';
      if (name.includes('android') || name.includes('mobile')) return 'fab fa-android text-success';
      if (name.includes('ipad') || name.includes('tablet')) return 'fas fa-tablet-alt text-info';
      if (name.includes('chrome')) return 'fab fa-chrome text-warning';
      if (name.includes('firefox')) return 'fab fa-firefox text-danger';
      if (name.includes('safari')) return 'fab fa-safari text-primary';
      if (name.includes('desktop')) return 'fas fa-desktop text-secondary';
      return 'fas fa-mobile-alt text-primary';
    },
    
    getDeviceStatus(device) {
      const lastLogin = new Date(device.last_login);
      const now = new Date();
      const diffHours = (now - lastLogin) / (1000 * 60 * 60);
      
      if (diffHours < 24) return 'Active';
      if (diffHours < 168) return 'Recent';
      return 'Inactive';
    },
    
    getDeviceStatusVariant(device) {
      const status = this.getDeviceStatus(device);
      switch (status) {
        case 'Active': return 'success';
        case 'Recent': return 'warning';
        case 'Inactive': return 'secondary';
        default: return 'secondary';
      }
    },
    
    getLoginCountVariant(count) {
      if (count >= 50) return 'danger';
      if (count >= 20) return 'warning';
      if (count >= 5) return 'info';
      return 'secondary';
    },
    
    getActivityPercentage(device) {
      if (!this.deviceAnalysis?.devices) return 0;
      const maxLogins = Math.max(...this.deviceAnalysis.devices.map(d => d.login_count));
      return maxLogins > 0 ? (device.login_count / maxLogins) * 100 : 0;
    },
    
    getActivityVariant(device) {
      const percentage = this.getActivityPercentage(device);
      if (percentage >= 80) return 'danger';
      if (percentage >= 50) return 'warning';
      if (percentage >= 20) return 'info';
      return 'success';
    },
    
    getDeviceRowClass(device) {
      const status = this.getDeviceStatus(device);
      switch (status) {
        case 'Active': return 'table-success';
        case 'Recent': return 'table-warning';
        case 'Inactive': return 'table-light';
        default: return '';
      }
    },
    
    getRecommendations() {
      if (!this.deviceAnalysis) return [];
      
      const recommendations = [];
      const riskLevel = this.deviceAnalysis.risk_level;
      const deviceCount = this.deviceAnalysis.device_count;
      
      if (riskLevel === 'HIGH') {
        recommendations.push({
          title: 'High Risk Account Detected',
          description: 'This user has an unusually high number of devices. Consider contacting them to verify account security and possibly require password reset.',
          icon: 'fas fa-exclamation-triangle text-danger',
          class: 'border-left-danger bg-light-danger'
        });
      }
      
      if (deviceCount >= 5) {
        recommendations.push({
          title: 'Multiple Device Access',
          description: 'Monitor this account for simultaneous logins from different devices, which could indicate credential sharing.',
          icon: 'fas fa-eye text-warning',
          class: 'border-left-warning bg-light-warning'
        });
      }
      
      if (riskLevel === 'MEDIUM') {
        recommendations.push({
          title: 'Moderate Risk Level',
          description: 'Keep monitoring this account. Consider implementing additional security measures if the pattern continues.',
          icon: 'fas fa-shield-alt text-info',
          class: 'border-left-info bg-light-info'
        });
      }
      
      if (riskLevel === 'LOW') {
        recommendations.push({
          title: 'Normal Usage Pattern',
          description: 'This account shows normal device usage patterns. Continue regular monitoring.',
          icon: 'fas fa-check-circle text-success',
          class: 'border-left-success bg-light-success'
        });
      }
      
      // Always add general security recommendation
      recommendations.push({
        title: 'Security Best Practices',
        description: 'Encourage the user to use strong, unique passwords and enable two-factor authentication if available.',
        icon: 'fas fa-lock text-primary',
        class: 'border-left-primary bg-light-primary'
      });
      
      return recommendations;
    }
  },
  
  async created() {
    if (this.userId) {
      await this.fetchDeviceAnalysis({
        userId: this.userId,
        daysBack: 30
      });
    }
  }
};
</script>

<style scoped>
.user-device-analysis {
  max-height: 80vh;
  overflow-y: auto;
}

.border-left-primary {
  border-left: 0.25rem solid #4e73df !important;
}

.border-left-success {
  border-left: 0.25rem solid #1cc88a !important;
}

.border-left-info {
  border-left: 0.25rem solid #36b9cc !important;
}

.border-left-warning {
  border-left: 0.25rem solid #f6c23e !important;
}

.border-left-danger {
  border-left: 0.25rem solid #e74a3b !important;
}

.text-xs {
  font-size: 0.75rem;
}

.recommendation-item {
  border: 1px solid #e3e6f0;
  border-left-width: 4px;
}

.bg-light-primary {
  background-color: rgba(78, 115, 223, 0.1) !important;
}

.bg-light-success {
  background-color: rgba(28, 200, 138, 0.1) !important;
}

.bg-light-info {
  background-color: rgba(54, 185, 204, 0.1) !important;
}

.bg-light-warning {
  background-color: rgba(246, 194, 62, 0.1) !important;
}

.bg-light-danger {
  background-color: rgba(231, 74, 59, 0.1) !important;
}

.table th {
  border-top: none;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  color: #858796;
}

.progress {
  background-color: rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .recommendation-item {
    padding: 1rem !important;
  }
}
</style> 